import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { FileService } from '../../../core/service/file.service';
import { FormControlExFactory } from '../../service/factory/form-control-ex-factory.service';
import { MessageService } from '../../service/message.service';
import { getCompressedImageFile } from '../../helper/image-compressor';
import { IsMobileDevice } from '../../helper/mobile';
import { OpenFileHelperService } from '../../service/open-file-helper.service';
import { LanguageService } from '../../service/language.service';
import { resolveAstralIconClass } from '../astral-components/astral-icon-class.util';
import { PhotoPreviewService } from '../../service/photo-preview-service';

@Component({
    selector: 'app-multi-file-upload',
    templateUrl: './multi-file-upload.component.html',
    styleUrls: ['./multi-file-upload.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MultiFileUploadComponent implements OnInit, OnDestroy {
  private readonly _openInNewTabDebounceMs = 500;
  private _lastOpenInNewTabAt = 0;
  readonly clearIconClass = resolveAstralIconClass('clear') ?? '';
  readonly uploadIconClass = resolveAstralIconClass('upload') ?? '';

  constructor(
    protected fb: FormBuilder,
    protected fc: FormControlExFactory,
    private readonly _fileService: FileService,
    private readonly _notificationService: MessageService,
    private readonly _languageService: LanguageService,
    private readonly _openFileHelperService: OpenFileHelperService,
    private readonly _photoPreviewService: PhotoPreviewService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}
  // Accept AbstractControl for template binding; used internally as a FormArray.
  private _forms: FormArray;
  @Input() set forms(value: AbstractControl) {
    this._forms = value as FormArray;
  }
  get forms(): FormArray {
    return this._forms;
  }
  @Input() maxFiles = 5;
  @Input() useAttachmentNumberAsTitle = false;
  @Input() text = 'COMMON.ATTACH_FILE';
  @Input() allowedMimeTypes: string[] = [];
  @Input() contentWidth = false;
  @Input() isOnSidebar: boolean = false;
  @Input() readonly: boolean = false;
  @Output() fileAdded = new EventEmitter<File>();
  @Output() fileRemoved = new EventEmitter<number>();
  @Output() processingChange = new EventEmitter<boolean>();
  @Input() maxFileSize: number = 20; // fair BE limit
  private _activeFileChanges = 0;
  private _destroyed = false;

  @ViewChild('uploadFile') uploadFileElement: ElementRef<HTMLInputElement>;

  get fileCount() {
    return this.forms?.controls?.length ?? 0;
  }

  @Input() externalClickEvent: Observable<void>;
  @Input() externalClearEvent: Observable<FormGroup>;
  private eventsSubscription: Subscription;

  private _sub = new Subscription();
  ngOnInit(): void {
    for (let form of this.forms.controls.map(f => f as FormGroup)) {
      if (this._fileService.isWebImage(form.controls['mimeType'].value) && form.controls['src']?.value) {
        this._sub.add(
          this._fileService.loadFileBlob(form.controls['src'].value).subscribe(file => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = _event => {
              form.controls['src'].setValue(reader.result);
            };
          })
        );
      }
    }

    this.eventsSubscription = this.externalClearEvent?.subscribe(value => {
      this.delete(value);
    });

    this._sub.add(
      this.externalClickEvent?.subscribe(() => {
        this.openFileDialog();
      })
    );
  }

  ngOnDestroy(): void {
    if (this._activeFileChanges > 0) this.processingChange.emit(false);
    this._destroyed = true;
    this.eventsSubscription?.unsubscribe();
    this._sub.unsubscribe();
  }

  openFileDialog(): void {
    if (this.readonly || this._destroyed) return;
    this.uploadFileElement?.nativeElement.click();
  }

  async onFileInputChange(files: FileList | null): Promise<void> {
    await this.onFileChange(files);
    if (this.uploadFileElement?.nativeElement) {
      this.uploadFileElement.nativeElement.value = '';
    }
  }

  clearAllFiles() {
    if (this.readonly || this._destroyed) return;
    this.forms.clear();
    this.forms.markAsDirty();
    this.forms.updateValueAndValidity();
    this.changeDetectorRef.detectChanges();
  }

  getClass(mimeType: string): string {
    if (this._fileService.isPdf(mimeType)) {
      return 't-pdf';
    }
    if (this._fileService.isSpreadsheet(mimeType)) {
      return 't-xls';
    }
    if (this._fileService.isWordProcessor(mimeType)) {
      return 't-doc';
    }
    if (!this._fileService.isWebImage(mimeType)) {
      return 't-other';
    }
    return 't-image';
  }

  showImage(mimeType: string) {
    return this._fileService.isWebImage(mimeType);
  }

  delete(form: FormGroup) {
    if (this.readonly || this._destroyed) return;
    let index = this.forms.controls.indexOf(form);
    this.forms.removeAt(index);
    this.forms.markAsDirty();
    this.forms.updateValueAndValidity();
    this.fileRemoved.emit(index);
    this.changeDetectorRef.detectChanges();
  }

  private _checkFileErrors(file: File): string {
    const fileLimit = this._getFileLimit(this.maxFileSize);
    if (file.size > fileLimit && !this._fileService.isWebImage(file.type)) {
      // big images will be compressed so their size not matters
    return this._languageService.instant('FILES.LIMIT_ERROR', { size: this.maxFileSize });
    }
    if (this._fileService.validateDocument(file) !== null) {
    return this._languageService.instant('FILES.UNSUPPORTED_MIME_ERROR');
    }
    if (this.allowedMimeTypes?.length > 0) {
    return this.allowedMimeTypes.includes(file.type) ? null : this._languageService.instant('FILES.INVALID_MIME_ERROR');
    }
    return null;
  }

  public previewFile = (formGroup: FormGroup) => {
    const mimeType = formGroup.controls['mimeType']?.value;
    if (this._fileService.isWebImage(mimeType)) {
      this._photoPreviewService.showPhotoPreview({
        src: formGroup.controls['src']?.value,
        fileName: formGroup.controls['originalName']?.value ?? formGroup.controls['title']?.value,
      });
      return;
    }

    const now = Date.now();
    if (now - this._lastOpenInNewTabAt < this._openInNewTabDebounceMs) {
      return;
    }
    this._lastOpenInNewTabAt = now;

    this._openFileHelperService.openBlobInNewTab(formGroup.controls['data']?.value, formGroup.controls['mimeType']?.value);
  };

  private _getFileLimit(limitInMegabytes: number): number {
    return limitInMegabytes * 1024 * 1024;
  }

  async onFileChange(files: FileList | null) {
    // FileList is live in some browsers; snapshot it before asynchronous image compression.
    const selectedFiles = files ? Array.from(files) : [];
    if (selectedFiles.length > 0 && !this.readonly && !this._destroyed) {
      this._activeFileChanges++;
      this.processingChange.emit(true);
      try {
        for (const selectedFile of selectedFiles) {
          if (this.readonly || this._destroyed) return;
          if (this.fileCount >= this.maxFiles) return;

          let file = selectedFile;
          const error = this._checkFileErrors(file);
          if (error) {
            this._notificationService.showError(error);
          } else {
            let dataUrl = '';
            if (this._fileService.isWebImage(file.type)) {
              const fileData = await getCompressedImageFile(file);
              file = fileData?.file;
              dataUrl = fileData?.dataUrl;
            }
            if (this.readonly || this._destroyed) return;

            const originalName = file.name;
            const lastDot = originalName.lastIndexOf('.');
            const ext = lastDot === -1 ? '' : originalName.slice(lastDot); // includes dot

            let displayName = originalName;
            if (this._fileService.isWebImage(file.type) && IsMobileDevice()) {
              const d = new Date();
              const datePrefix = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
              displayName = `${datePrefix}${ext}`;
            }

            const fileForm = this.fb.group({
              id: this.fc.createHidden(null),
              data: this.fc.createHidden(file),
              src: this.fc.createHidden(dataUrl),
              mimeType: this.fc.createHidden(file.type),
              size: this.fc.createHidden(file.size),
              title: this.fc.createMandatory('DOCUMENT.CAPTION', this.useAttachmentNumberAsTitle ? `Attachment ${this.fileCount}` : displayName),
              originalName: this.fc.createHidden(displayName),
            });
            this.forms.push(fileForm);
            this.forms.markAsDirty();
            const hasPreview = !!fileForm.controls['src'].value;
            if (!hasPreview) {
              const reader = new FileReader();
              reader.onload = () => {
                if (this._destroyed) return;
                fileForm.controls['src'].setValue(reader.result);
                this.changeDetectorRef.detectChanges();
              };
              reader.readAsDataURL(file);
            }

            this.forms.updateValueAndValidity();
            this.fileAdded.emit(file);
            this.changeDetectorRef.detectChanges();
          }
        }
      } finally {
        this._activeFileChanges--;
        if (!this._destroyed) this.processingChange.emit(this._activeFileChanges > 0);
      }
    }
  }
}
